import React, {FC, ReactNode} from 'react';
import {observer} from 'mobx-react-lite';
import {withViewModel} from '@joyrideautos/ui-common/src/utils/componentUtils';
import {HasViewModel} from '@joyrideautos/ui-models/src/types';
import {BottomBannerEnum, BottomBannerViewModel} from './BottomBannerViewModel';
import BannerTemplate from './bannerTemplate/BannerTemplate';
import {Button} from 'antd';
import {WarningOutlined} from '@ant-design/icons';
import {Router, useRouter} from '@joyrideautos/web-common-components/src/router/Router';
import {Routes} from '../../../Routes';
import {rootStore} from '@models/stores/RootStore';
import {TFunction} from 'i18next';
import {Trans, useTranslation} from 'react-i18next';
import styles from './BottomBanner.module.less';
import {URL_CONTACT_SUPPORT, URL_SUPPORT_BIDDING_LIMITS_GLOBAL} from '@joyrideautos/auction-core/src/constants/urls';
import {AddPaymentModal} from '@pageComponents/AddPaymentModal';
import {SUPPORT_EMAIL_ADDRESS} from '@joyrideautos/auction-core/src/constants/Constants';

const bottomBanner: {
    [banner in BottomBannerEnum]: (viewModel: any, router: Router, t: TFunction<'en'>) => ReactNode;
} = {
    [BottomBannerEnum.COMPLETE_PURCHASE]: (viewModel, router, t) => (
        <BannerTemplate
            tooltip={t('banner.completePurchase.tooltip')}
            title={t('banner.completePurchase.title')}
            subTitle={
                <Trans
                    i18nKey="banner.completePurchase.subTitle"
                    values={{time: viewModel.currentViewModel.timeText}}
                    components={{strong: <strong />}}
                />
            }
            button={
                <Button onClick={() => router.push(Routes.CHECKOUT_SUMMARY())}>
                    {t('banner.completePurchase.buttonLabel')}
                </Button>
            }
        />
    ),
    [BottomBannerEnum.OVERDUE]: (viewModel, router, t) => (
        <BannerTemplate
            tooltip={t('banner.overdue.tooltip')}
            title={t('banner.overdue.title')}
            subTitle={
                <Trans
                    i18nKey="banner.overdue.subTitle"
                    values={{time: viewModel.currentViewModel.timeText}}
                    components={{strong: <strong />}}
                />
            }
            button={
                <Button onClick={() => router.push(Routes.CHECKOUT_SUMMARY())}>
                    {t('banner.overdue.buttonLabel')}
                </Button>
            }
        />
    ),
    [BottomBannerEnum.EXPIRED]: (viewModel, router, t) => (
        <BannerTemplate
            tooltip={t('banner.expired.tooltip')}
            icon={<WarningOutlined />}
            title={t('banner.expired.title')}
            subTitle={
                <Trans
                    i18nKey="banner.expired.subTitle"
                    components={{
                        CustomLink: (
                            <a href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}>
                                <u>{SUPPORT_EMAIL_ADDRESS}</u>
                            </a>
                        ),
                    }}
                />
            }
            button={
                <a className="a-tag-button" href={URL_CONTACT_SUPPORT} rel="noopener noreferrer" target="_blank">
                    {t('banner.expired.buttonLabel')}
                </a>
            }
            className="expired"
        />
    ),
    [BottomBannerEnum.ADD_PAYMENT_METHOD]: (viewModel, router, t) => (
        <BannerTemplate
            title={t('banner.addPaymentMethod.title')}
            subTitle={<span>{t('banner.addPaymentMethod.subTitle')}</span>}
            button={
                <Button onClick={() => viewModel.togglePaymentModal()}>
                    {t('banner.addPaymentMethod.buttonLabel')}
                </Button>
            }
        />
    ),
    [BottomBannerEnum.BIDDING_LIMIT_REACHED]: (viewModel, router, t) =>
        viewModel.currentViewModel.loggedInUserHasOutstandingPayments ? (
            <BannerTemplate
                title={t('banner.balanceThresholdReached.title')}
                subTitle={<span>{t('banner.balanceThresholdReached.subTitle')}</span>}
                button={
                    <Button onClick={() => router.push(Routes.CHECKOUT_SUMMARY())}>
                        {t('banner.balanceThresholdReached.payButtonLabel')}
                    </Button>
                }
            />
        ) : (
            <BannerTemplate
                title={t('banner.balanceThresholdReached.title')}
                button={
                    <Button href={URL_SUPPORT_BIDDING_LIMITS_GLOBAL} rel="noreferrer" target="_blank">
                        {t('common.learnMore')}
                    </Button>
                }
            />
        ),
};

interface BottomBannerProps extends HasViewModel<typeof BottomBannerViewModel> {}

const BottomBanner: FC<BottomBannerProps> = observer(({viewModel}) => {
    const router = useRouter();
    const {t} = useTranslation();
    const {currentBanner} = viewModel;

    return (
        <>
            <AddPaymentModal
                handleCancel={() => viewModel.togglePaymentModal()}
                isOpen={!!viewModel.isPaymentModalOpen}
                paymentMethodTypes={viewModel.paymentMethodTypes || []}
            />
            {currentBanner ? (
                <div className={styles['bottom-banner']}>{bottomBanner[currentBanner](viewModel, router, t)}</div>
            ) : null}
        </>
    );
});

export default withViewModel(BottomBannerViewModel, rootStore)(BottomBanner);
